<template>
    <div v-if="visible" class="OpenVerificationBanner" data-test-id="OpenVerificationBanner">
        <div class="OpenVerificationBanner__header">
            <div
                class="OpenVerificationBanner__toggle"
                role="button"
                tabindex="0"
                :aria-expanded="expanded ? 'true' : 'false'"
                data-test-id="OpenVerificationBanner__toggle"
                @click="toggle"
                @keydown.enter.prevent="toggle"
                @keydown.space.prevent="toggle"
            >
                <EidIcon
                    class="OpenVerificationBanner__chevron"
                    :icon-src="expanded ? faAngleDown : faAngleRight"
                />
                <div class="OpenVerificationBanner__summary">
                    <span class="OpenVerificationBanner__eyebrow" data-test-id="OpenVerificationBanner__eyebrow">
                        <span class="OpenVerificationBanner__dot" />
                        Current open verification · Requested {{ requestedAtLabel }}
                    </span>
                    <span class="OpenVerificationBanner__title" data-test-id="OpenVerificationBanner__title">
                        {{ receivedRows.length }} of {{ rows.length }} responses received
                    </span>
                    <span class="OpenVerificationBanner__subtext" data-test-id="OpenVerificationBanner__subtext">
                        {{ waitingText }}
                    </span>
                </div>
            </div>
            <RbacDisplayGuard v-if="canForce" :features="[ RbacFeature.entityDetailsForceSubmission ]">
                <span
                    v-if="forceRefusal"
                    class="OpenVerificationBanner__forceUnavailable"
                    data-test-id="OpenVerificationBanner__forceUnavailable"
                >
                    {{ forceUnavailableText }}
                </span>
                <NewButton
                    v-else
                    variant="outline"
                    color="primary"
                    size="sm"
                    data-test-id="OpenVerificationBanner__forceButton"
                    @click="onForce"
                >
                    Force Processing
                </NewButton>
            </RbacDisplayGuard>
        </div>
        <div class="OpenVerificationBanner__progress">
            <span
                v-for="row in rows"
                :key="row.key"
                class="OpenVerificationBanner__segment"
                :class="{ 'OpenVerificationBanner__segment--received': row.received }"
                data-test-id="OpenVerificationBanner__segment"
            />
        </div>
        <div v-if="expanded" class="OpenVerificationBanner__list" data-test-id="OpenVerificationBanner__list">
            <span class="OpenVerificationBanner__listTitle">Responses in this verification</span>
            <div
                v-for="row in rows"
                :key="row.key"
                class="OpenVerificationBanner__row"
                data-test-id="OpenVerificationBanner__row"
            >
                <span class="OpenVerificationBanner__rowName">{{ row.name }}</span>
                <span
                    class="OpenVerificationBanner__tag"
                    :class="{ 'OpenVerificationBanner__tag--received': row.received }"
                >
                    {{ row.received ? 'Response collected' : 'Awaiting response' }}
                </span>
                <span class="OpenVerificationBanner__rowDate">{{ row.dateLabel }}</span>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
    .OpenVerificationBanner {
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding: 16px 20px;
        margin-bottom: 16px;
        background-color: var(--eidSafetyBlue-10);
        border: 1px solid var(--eidSafetyBlue-25);
        border-radius: 8px;

        &__header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 16px;
        }

        &__toggle {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            min-width: 0;
            cursor: pointer;
        }

        &__chevron {
            flex: none;
            width: 16px;
            margin-top: 2px;
            color: var(--eidSecureBlue);
        }

        &__summary {
            display: flex;
            flex-direction: column;
            gap: 2px;
            min-width: 0;
        }

        &__eyebrow {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 4px;
            font-size: var(--text-caption-size, 11.5px);
            font-weight: 500;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            color: var(--eidSafetyBlue);
        }

        &__dot {
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background-color: var(--eidSafetyBlue);
        }

        &__title {
            font-size: var(--text-section-header-size, 15.5px);
            font-weight: 500;
            color: var(--eidSecureBlue);
        }

        &__subtext {
            font-size: var(--text-small-size, 13px);
            color: var(--eidSecureBlue-75);
        }

        &__forceUnavailable {
            max-width: 280px;
            font-size: var(--text-small-size, 13px);
            color: var(--eidSecureBlue-75);
            text-align: right;
        }

        &__progress {
            display: flex;
            gap: 4px;
        }

        &__segment {
            flex: 1;
            height: 6px;
            border-radius: 3px;
            background-color: var(--eidTechGray-25);

            &--received {
                background-color: var(--eidSafetyBlue);
            }
        }

        &__list {
            display: flex;
            flex-direction: column;
            margin-top: 4px;
            border-top: 1px solid var(--eidTechGray-10);
        }

        &__listTitle {
            padding: 12px 0 4px;
            font-size: var(--text-small-size, 12px);
            color: var(--eidTechGray);
        }

        &__row {
            display: grid;
            grid-template-columns: minmax(0, 1.2fr) auto minmax(0, 1.4fr);
            column-gap: 16px;
            align-items: center;
            min-height: 40px;
            padding: 6px 0;
            box-sizing: border-box;
            border-bottom: 1px solid var(--eidTechGray-10);
            font-size: var(--text-body-size, 13.5px);
            line-height: 1.4;
        }

        &__rowName {
            font-weight: 500;
            color: var(--eidSecureBlue);
        }

        &__tag {
            display: inline-flex;
            align-items: center;
            height: 22px;
            padding: 0 8px;
            border-radius: 4px;
            background-color: var(--eidTechGray-10);
            color: var(--eidSecureBlue);
            font-size: var(--text-small-size, 13px);
            font-weight: 500;
            white-space: nowrap;

            &--received {
                background-color: var(--eidSafetyBlue-25);
            }
        }

        &__rowDate {
            color: var(--eidSecureBlue-75);
        }
    }
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { faAngleDown, faAngleRight } from '@fortawesome/pro-light-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import RbacDisplayGuard
        from '@evidentid/dashboard-commons/modules/role-base-access-control/components/rbac-guard/RbacDisplayGuard.vue';
    import { TprmCredentialType } from '@evidentid/tprm-portal-lib/models/common';
    import {
        ForceSubmissionConflictReason,
        OpenVerification,
        OpenVerificationRequirement,
        TprmRequirementModel,
    } from '@evidentid/tprm-portal-lib/models/entity-details';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';

    import NewButton from '@/components/new-button/NewButton.vue';
    import { getForceSubmissionUnavailableText } from '@/modules/entity-details/constants/forceSubmission.constants';
    import { formatDateTime } from '@/utils/formatDateTime';

    import {
        ForceSubmissionRequest,
        getOpenVerificationRequirementName,
        isRequirementReceived,
    } from './helpers';

    interface OpenVerificationBannerRow {
        key: string;
        name: string;
        verificationType: OpenVerificationRequirement['verificationType'];
        received: boolean;
        dateLabel: string;
    }

    export default defineComponent({
        name: 'OpenVerificationBanner',
        components: { EidIcon, NewButton, RbacDisplayGuard },
        props: {
            openVerification: {
                type: Object as PropType<OpenVerification | null>,
                default: null,
            },
            requirementModels: {
                type: Array as PropType<TprmRequirementModel[]>,
                default: (): TprmRequirementModel[] => [],
            },
            credentialTypes: {
                type: Array as PropType<TprmCredentialType[]>,
                default: (): TprmCredentialType[] => [],
            },
        },
        emits: [ 'force-submission' ],
        data() {
            return {
                expanded: false,
                faAngleDown,
                faAngleRight,
                RbacFeature,
            };
        },
        computed: {
            requestedAtLabel(): string {
                return formatDateTime(this.openVerification?.requestedAt);
            },
            // Received first, each group keeping the order the API sent.
            rows(): OpenVerificationBannerRow[] {
                const requirements = this.openVerification?.requirements || [];
                const toRow = (received: boolean) => (requirement: typeof requirements[number]) => ({
                    key: `${requirement.coverageType}-${requirement.instanceId || ''}`,
                    name: getOpenVerificationRequirementName(
                        requirement, this.requirementModels, this.credentialTypes,
                    ),
                    verificationType: requirement.verificationType,
                    received,
                    dateLabel: received
                        ? `Received ${formatDateTime(requirement.receivedAt)}`
                        : `Requested ${this.requestedAtLabel}`,
                });
                return [
                    ...requirements.filter(isRequirementReceived).map(toRow(true)),
                    ...requirements.filter((requirement) => !isRequirementReceived(requirement)).map(toRow(false)),
                ];
            },
            receivedRows(): OpenVerificationBannerRow[] {
                return this.rows.filter((row) => row.received);
            },
            awaitingRows(): OpenVerificationBannerRow[] {
                return this.rows.filter((row) => !row.received);
            },
            visible(): boolean {
                return this.receivedRows.length > 0;
            },
            canForce(): boolean {
                return this.receivedRows.length > 0 && this.awaitingRows.length > 0;
            },
            // The button is withheld whenever the server would refuse it, with the reason in its place.
            forceRefusal(): ForceSubmissionConflictReason | null {
                return this.openVerification?.forceSubmissionRefusal || null;
            },
            forceUnavailableText(): string {
                return this.forceRefusal ? getForceSubmissionUnavailableText(this.forceRefusal) : '';
            },
            waitingText(): string {
                return this.awaitingRows.length > 0
                    ? `Waiting on ${this.awaitingRows.map((row) => row.name).join(', ')}`
                    : 'All responses received';
            },
        },
        methods: {
            toggle(): void {
                this.expanded = !this.expanded;
            },
            onForce(): void {
                if (!this.openVerification) {
                    return;
                }
                const request: ForceSubmissionRequest = {
                    verificationId: this.openVerification.verificationId,
                    receivedCount: this.receivedRows.length,
                    awaiting: this.awaitingRows.map((row) => ({
                        name: row.name,
                        verificationType: row.verificationType,
                    })),
                };
                this.$emit('force-submission', request);
            },
        },
    });
</script>
